import { useCallback, useEffect, useRef, useState } from 'react';
import { liveQuery } from 'dexie';
import { useOnlineStatus } from './ConnectivityContext';
import { drainQueue } from './syncEngine';
import { db } from './db';
import { AUTHENTICATED_EVENT } from '../auth/installAuthFetch';

// Drains the offline sync queue whenever connectivity transitions from
// offline -> online, then calls `onSynced` (typically the screen's
// `fetchData()`) so optimistic local entries get reconciled with the
// server-canonical copies. Also exposes live queue state for UI badges/banner.
//
// wasOnlineRef always starts at false (not the current isOnline value) so a
// page reload that happens to land after connectivity is already back still
// counts as a "transition" on the first effect run, and triggers a drain —
// otherwise a reload/tab-loss while offline followed by network coming back
// before the page finishes loading would leave the queue stuck until the
// user manually toggled connectivity to create a real edge.
//
// A 401 (expired session) stops the drain without failing the item:
// `authRequired` then stays true until the user logs back in, which fires
// walter:authenticated and drains again.
export function useSyncOnReconnect(onSynced) {
    const { isOnline } = useOnlineStatus();
    const wasOnlineRef = useRef(false);
    const [syncProgress, setSyncProgress] = useState(null);
    const [queueCount, setQueueCount] = useState(0);
    const [hasErrors, setHasErrors] = useState(false);
    const [authRequired, setAuthRequired] = useState(false);

    const runDrain = useCallback(async () => {
        const result = await drainQueue({ onProgress: setSyncProgress });
        setSyncProgress(null);
        setAuthRequired(Boolean(result.authRequired));
        if (result.drained > 0 && onSynced) {
            await onSynced();
        }
        return result;
    }, [onSynced]);

    useEffect(() => {
        const wasOnline = wasOnlineRef.current;
        wasOnlineRef.current = isOnline;
        if (isOnline && !wasOnline) {
            runDrain();
        }
    }, [isOnline, runDrain]);

    useEffect(() => {
        window.addEventListener(AUTHENTICATED_EVENT, runDrain);
        return () => window.removeEventListener(AUTHENTICATED_EVENT, runDrain);
    }, [runDrain]);

    useEffect(() => {
        const subscription = liveQuery(() => db.syncQueue.toArray()).subscribe({
            next: (rows) => {
                setQueueCount(rows.length);
                setHasErrors(rows.some((row) => row.status === 'error'));
            },
            error: (err) => console.error('Erreur de lecture de la file de synchronisation:', err),
        });
        return () => subscription.unsubscribe();
    }, []);

    return { syncProgress, queueCount, hasErrors, authRequired, retrySync: runDrain };
}
